import { useEffect, useState } from 'react';
import { Alert, Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { api } from './api';
import { QrImage, type Payee } from './PayeeInfo';

/**
 * Where this enterprise wants to be paid. Partners see it when paying an order; the platform
 * only shows it and never moves the money.
 */
export default function PaymentProfileCard(p: {
  workspace: string;
  canEdit: boolean;
  notify: (message: string) => void;
}) {
  const [profile, setProfile] = useState<Payee | null>(null);
  const [form, setForm] = useState({ bankHolder: '', bankName: '', bankAccount: '', note: '' });
  const [qrVersion, setQrVersion] = useState(0);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const load = () =>
    api<Payee>('/payment-profile')
      .then((data) => {
        setProfile(data);
        setForm({
          bankHolder: data.bankHolder,
          bankName: data.bankName,
          bankAccount: data.bankAccount,
          note: data.note,
        });
      })
      .catch((e) => setError(e.message));
  useEffect(() => {
    void load();
  }, [p.workspace]);

  async function run(task: () => Promise<unknown>, message: string) {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await task();
      await load();
      p.notify(message);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const upload = (kind: 'wechat' | 'alipay', file: File) =>
    run(async () => {
      const body = new FormData();
      body.append('file', file);
      await api(`/payment-profile/qr/${kind}`, body);
      setQrVersion((v) => v + 1);
    }, '收款码已更新');

  const qrSlot = (kind: 'wechat' | 'alipay', label: string) => {
    const has = kind === 'wechat' ? profile?.hasWechatQr : profile?.hasAlipayQr;
    return (
      <Box sx={{ textAlign: 'center' }}>
        {has ? (
          <QrImage workspaceId={p.workspace} kind={kind} version={qrVersion} />
        ) : (
          <Box
            sx={{
              width: 148,
              height: 148,
              borderRadius: 1,
              border: '1px dashed #c9d6d2',
              display: 'grid',
              placeItems: 'center',
              color: 'text.secondary',
              fontSize: 12,
            }}
          >
            未上传
          </Box>
        )}
        <Typography sx={{ fontSize: 12.5, mt: 0.75 }}>{label}</Typography>
        {p.canEdit && (
          <Stack direction="row" spacing={0.5} sx={{ justifyContent: 'center', mt: 0.5 }}>
            <Button size="small" component="label" disabled={busy}>
              {has ? '更换' : '上传'}
              <input
                hidden
                type="file"
                accept="image/png,image/jpeg"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void upload(kind, file);
                  e.target.value = '';
                }}
              />
            </Button>
            {has && (
              <Button
                size="small"
                color="error"
                disabled={busy}
                onClick={() =>
                  run(() => api(`/payment-profile/qr/${kind}/remove`, {}), '已移除收款码')
                }
              >
                移除
              </Button>
            )}
          </Stack>
        )}
      </Box>
    );
  };

  return (
    <Paper sx={{ mb: 3 }}>
      <div className="panel-title">
        <Box>
          <Typography sx={{ fontSize: 15, fontWeight: 650 }}>收款信息</Typography>
          <Typography color="text.secondary" sx={{ fontSize: 12, mt: 0.5 }}>
            客户付款时会看到这里的账户和收款码。平台只展示，不经手资金；仅已合作的企业可见。
          </Typography>
        </Box>
      </div>
      <Box sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto' },
            gap: 4,
            alignItems: 'start',
          }}
        >
          <Stack spacing={2}>
            <Typography sx={{ fontSize: 13, fontWeight: 650 }}>对公账户</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="户名"
                value={form.bankHolder}
                disabled={!p.canEdit || busy}
                onChange={(e) => setForm({ ...form, bankHolder: e.target.value })}
              />
              <TextField
                label="开户行"
                value={form.bankName}
                disabled={!p.canEdit || busy}
                onChange={(e) => setForm({ ...form, bankName: e.target.value })}
              />
            </Box>
            <TextField
              label="账号"
              value={form.bankAccount}
              disabled={!p.canEdit || busy}
              onChange={(e) => setForm({ ...form, bankAccount: e.target.value })}
            />
            <TextField
              label="付款备注要求（选填）"
              placeholder="例如：转账请备注订单号"
              value={form.note}
              disabled={!p.canEdit || busy}
              slotProps={{ inputLabel: { shrink: true } }}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
            />
            {p.canEdit && (
              <Box>
                <Button
                  variant="contained"
                  disabled={busy}
                  onClick={() => run(() => api('/payment-profile', form), '收款信息已保存')}
                >
                  保存收款信息
                </Button>
              </Box>
            )}
          </Stack>
          <Stack spacing={2}>
            <Typography sx={{ fontSize: 13, fontWeight: 650 }}>收款码（选填）</Typography>
            <Stack direction="row" spacing={3}>
              {qrSlot('wechat', '微信收款码')}
              {qrSlot('alipay', '支付宝收款码')}
            </Stack>
          </Stack>
        </Box>
        {!p.canEdit && (
          <Typography color="text.secondary" sx={{ fontSize: 12, mt: 2 }}>
            收款信息由企业管理员或财务维护。
          </Typography>
        )}
      </Box>
    </Paper>
  );
}
